import { useStore } from '@tanstack/react-store'
import { Store } from '@tanstack/store'
import jwt_decode from 'jwt-decode'

import { UserInfo } from 'core/types/generated/schema/types'

import makeStorage from '../storage/makeStorage' // Adjust path as necessary
import { envStore, STANDARD_ENVIRONMENTS, Env as EnvType } from '../stores/env' // Import envStore, STANDARD_ENVIRONMENTS, and EnvType

import * as rawAuthApi from './authApi'

// --- Helper function to resolve apiBase from envStore ---
export const getApiBaseFromEnvStore = (
  envIdForLookup?: string,
): string | undefined => {
  const { activeEnvRef, customEnvs } = envStore.state
  if (!activeEnvRef) return undefined

  const envIdToUse = envIdForLookup || activeEnvRef.id
  const envTypeToUse = activeEnvRef.type

  if (envIdForLookup) {
    // Determine if standard or custom based on envIdForLookup format or presence in lists
    // This is a bit tricky as envStore's structure is { activeEnvRef: { type, id }, customEnvs: [...] }
    // We might need to iterate STANDARD_ENVIRONMENTS and customEnvs to find apiBase for envIdForLookup
    if (activeEnvRef.type === 'standard') {
      const standardEnv = STANDARD_ENVIRONMENTS.find(
        (env) => env.id === activeEnvRef.id,
      )
      return standardEnv?.apiBase
    }
    if (activeEnvRef.type === 'custom') {
      const customEnv = customEnvs.find(
        (env: EnvType) => env.id === activeEnvRef.id,
      )
      return customEnv?.apiBase
    }
  }

  if (envTypeToUse === 'standard') {
    const standardEnv = STANDARD_ENVIRONMENTS.find(
      (env) => env.id === envIdToUse,
    )
    return standardEnv?.apiBase
  }
  if (envTypeToUse === 'custom') {
    const customEnv = customEnvs.find((env: EnvType) => env.id === envIdToUse)
    return customEnv?.apiBase
  }
  return undefined
}

// Types (consider moving to a shared types.ts if used elsewhere)
export type DecodedToken = {
  exp?: number
  iat?: number
  personId?: string
  sub?: string // Typically the user ID
  companyId?: string
  employeeId?: string // ADDED OPTIONAL employeeId
  userType?: string // ADDED OPTIONAL userType
}

export type AuthStoreState = {
  username?: string
  lastAuthMethod?: string
  token?: string
  apiKey?: string
  isSessionActive: boolean // NEW: True if a session is considered active (token/apiKey present)

  // Loading states
  isInitializing: boolean // For the new initial validation/fetch step
  isLoggingIn: boolean
  isRenewing: boolean
  isValidatingApiKey: boolean
  isFetchingUser: boolean

  // User and session details
  decodedToken?: DecodedToken
  expiresAt?: number // Token expiration timestamp (ms)
  issuedAt?: number // Token issuance timestamp (ms)
  personId?: string
  companyId?: string
  userId?: string
  currentUser?: UserInfo['data'] // Placeholder for fetched user info
  authError?: any // To store any relevant auth error
  loginModalExplicitlyRequested: boolean // ADDED: For AppEnvMenu to signal LoginModal
}

// --- Storage Configuration ---
export const AUTH_STORAGE_NAMESPACE = 'peach-auth-global'
// New migration flag for multi-environment session structure
export const MIGRATION_MULTI_ENV_SESSIONS_FLAG_KEY =
  'peach-auth-migrated-to-multi-env-sessions-v1'

const globalAuthStorage = makeStorage(AUTH_STORAGE_NAMESPACE)

// Type for session data stored per environment
type EnvSessionData = {
  token?: string | null
  apiKey?: string | null
  username?: string | null
  lastAuthMethod?: string | null
  // We won't store currentUser per env in localStorage to keep it simpler,
  // it will be fetched on session initialization for the active env.
}

type StoredSessions = {
  [envId: string]: EnvSessionData
}

export const getInitialState = (): AuthStoreState => {
  let sessionsByEnv: StoredSessions = {}
  const migratedToMultiEnv = globalAuthStorage.get(
    MIGRATION_MULTI_ENV_SESSIONS_FLAG_KEY,
  )

  if (migratedToMultiEnv) {
    sessionsByEnv = globalAuthStorage.get('sessionsByEnv') || {}
  } else {
    console.info('AuthStore: Multi-environment session migration starting.')
    const migratedSessions: StoredSessions = {}

    const allEnvIds: Array<string> = []
    try {
      STANDARD_ENVIRONMENTS.forEach((env) => allEnvIds.push(env.id))
      // Ensure customEnvs is an array before trying to iterate
      const customEnvsList = Array.isArray(envStore.state.customEnvs)
        ? envStore.state.customEnvs
        : []
      customEnvsList.forEach((env: EnvType) => allEnvIds.push(env.id))
    } catch (e) {
      console.error(
        'AuthStore: Error accessing envStore.state for migration (allEnvIds).',
        e,
      )
    }

    const uniqueEnvIds = [...new Set(allEnvIds)]

    uniqueEnvIds.forEach((envId) => {
      try {
        const oldEnvSpecificStorage = makeStorage(envId)
        const token = oldEnvSpecificStorage.get('token')
        const apiKey = oldEnvSpecificStorage.get('apiKey')
        const username = oldEnvSpecificStorage.get('username')
        const lastAuthMethod = oldEnvSpecificStorage.get('lastAuthMethod')

        if (token || apiKey || username || lastAuthMethod) {
          migratedSessions[envId] = {
            token: token || undefined,
            apiKey: apiKey || undefined,
            username: username || undefined,
            lastAuthMethod: lastAuthMethod || undefined,
          }
          console.info(`AuthStore: Migrated data for envId "${envId}".`)
          oldEnvSpecificStorage.set('token', undefined)
          oldEnvSpecificStorage.set('apiKey', undefined)
          oldEnvSpecificStorage.set('username', undefined)
          oldEnvSpecificStorage.set('lastAuthMethod', undefined)
        }
      } catch (e) {
        console.error(
          `AuthStore: Error migrating data for envId "${envId}".`,
          e,
        )
      }
    })

    // Handling for the brief period where there was a single global token
    // As per user, this scenario is not a concern for released versions,
    // but keeping the cleanup logic for robustness during development if that state was hit.
    const oldGlobalToken = globalAuthStorage.get('token')
    const oldGlobalApiKey = globalAuthStorage.get('apiKey')
    const oldGlobalUsername = globalAuthStorage.get('username')
    const oldGlobalLastAuthMethod = globalAuthStorage.get('lastAuthMethod')
    const oldSingleGlobalMigrationFlag = globalAuthStorage.get(
      'peach-auth-migrated-to-global',
    )

    if (oldSingleGlobalMigrationFlag && (oldGlobalToken || oldGlobalApiKey)) {
      let associatedEnvId: string | undefined
      try {
        associatedEnvId = envStore.state.activeEnvRef?.id
      } catch (e) {
        console.error(
          'AuthStore: Could not get active env ID during final stage of migration check.',
          e,
        )
      }

      if (
        associatedEnvId &&
        !migratedSessions[associatedEnvId] &&
        (oldGlobalToken || oldGlobalApiKey)
      ) {
        console.info(
          `AuthStore: Found legacy single global token/apiKey, associating with currently active env "${associatedEnvId}" for migration.`,
        )
        migratedSessions[associatedEnvId] = {
          token: oldGlobalToken || undefined,
          apiKey: oldGlobalApiKey || undefined,
          username: oldGlobalUsername || undefined,
          lastAuthMethod: oldGlobalLastAuthMethod || undefined,
        }
      } else if (associatedEnvId && migratedSessions[associatedEnvId]) {
        console.info(
          `AuthStore: Legacy single global token/apiKey found, but env "${associatedEnvId}" already has migrated data. Prioritizing specific env migration.`,
        )
      } else if (oldGlobalToken || oldGlobalApiKey) {
        // If token exists but no associated env ID, log warning
        console.warn(
          'AuthStore: Legacy single global token/apiKey found, but no active env to associate it with or env already has data. This session data might be orphaned if not also in an old env-specific store.',
        )
      }
    }
    // Clear these old global keys regardless, as they are now part of sessionsByEnv or deprecated
    globalAuthStorage.set('token', undefined)
    globalAuthStorage.set('apiKey', undefined)
    globalAuthStorage.set('username', undefined)
    globalAuthStorage.set('lastAuthMethod', undefined)
    globalAuthStorage.set('currentUser', undefined)
    globalAuthStorage.set('peach-auth-migrated-to-global', undefined)

    sessionsByEnv = migratedSessions
    globalAuthStorage.set('sessionsByEnv', sessionsByEnv)
    globalAuthStorage.set(MIGRATION_MULTI_ENV_SESSIONS_FLAG_KEY, true)
    console.info(
      'AuthStore: Multi-environment session migration complete. New structure saved.',
    )
  }

  let currentToken: string | undefined | null
  let currentApiKey: string | undefined | null
  let currentUsername: string | undefined | null
  let currentLastAuthMethod: string | undefined | null

  const activeEnvId = envStore.state.activeEnvRef?.id

  if (activeEnvId && sessionsByEnv[activeEnvId]) {
    const activeEnvSession = sessionsByEnv[activeEnvId]
    currentToken = activeEnvSession.token
    currentApiKey = activeEnvSession.apiKey
    currentUsername = activeEnvSession.username
    currentLastAuthMethod = activeEnvSession.lastAuthMethod
    console.info(
      `AuthStore: Loaded session for active env "${activeEnvId}". Token: ${!!currentToken}, API Key: ${!!currentApiKey}`,
    )
  } else if (activeEnvId) {
    console.info(
      `AuthStore: No stored session found for active env "${activeEnvId}".`,
    )
  } else {
    console.info(
      'AuthStore: No active environment. Store will initialize sessionless.',
    )
  }

  let decoded: DecodedToken | undefined
  if (currentToken) {
    try {
      decoded = jwt_decode(currentToken)
    } catch (e) {
      console.error('AuthStore: Failed to decode token for active env.', e)
      if (activeEnvId && sessionsByEnv[activeEnvId]) {
        // sessionsByEnv[activeEnvId].token = undefined; // Don't persist this temporary clear yet
      }
      currentToken = null // Treat as invalid for current in-memory state
    }
  }

  return {
    username: currentUsername || undefined,
    lastAuthMethod: currentLastAuthMethod || undefined,
    token: currentToken || undefined,
    apiKey: currentApiKey || undefined,
    isSessionActive: !!(currentApiKey || currentToken),
    isInitializing: false,
    isLoggingIn: false,
    isRenewing: false,
    isValidatingApiKey: false,
    isFetchingUser: false,
    decodedToken: decoded,
    expiresAt: decoded?.exp ? decoded.exp * 1000 : undefined,
    issuedAt: decoded?.iat ? decoded.iat * 1000 : undefined,
    personId: decoded?.personId,
    companyId: decoded?.companyId,
    userId: decoded?.sub,
    currentUser: undefined,
    authError: null,
    loginModalExplicitlyRequested: false,
  }
}

// Initialize the store
export const authStore = new Store<AuthStoreState>(getInitialState())

// --- Helper function to update state after token/apiKey changes ---
const _updateStateAfterAuthChange = (
  newStateProperties: Partial<AuthStoreState>,
) => {
  const activeEnvId = envStore.state.activeEnvRef?.id

  authStore.setState((state) => {
    let tokenToDecode =
      newStateProperties.token !== undefined
        ? newStateProperties.token
        : state.token
    if (newStateProperties.token === null) tokenToDecode = undefined

    let decoded: DecodedToken | undefined
    if (tokenToDecode) {
      try {
        decoded = jwt_decode(tokenToDecode)
      } catch (e) {
        console.error('AuthStore: Token decode error during state update.', e)
        tokenToDecode = undefined
        // If new token was being set and is bad, clear it from current env session in sessionsByEnv
        // This requires fetching sessionsByEnv, updating, and saving - complex for a sync update part
        // For now, local state will clear. Persisted bad token will be cleared on next load if still bad.
      }
    }

    const finalToken = tokenToDecode
    const finalApiKey =
      newStateProperties.apiKey !== undefined
        ? newStateProperties.apiKey
        : state.apiKey
    const finalUsername =
      newStateProperties.username !== undefined
        ? newStateProperties.username
        : state.username
    const finalLastAuthMethod =
      newStateProperties.lastAuthMethod !== undefined
        ? newStateProperties.lastAuthMethod
        : state.lastAuthMethod

    // Persist to localStorage under the active environment ID
    if (activeEnvId) {
      const sessions = globalAuthStorage.get('sessionsByEnv') || {}
      if (!sessions[activeEnvId] && (finalToken || finalApiKey)) {
        sessions[activeEnvId] = {} // Initialize if new session for this env
      }

      if (sessions[activeEnvId]) {
        // Only update if session object exists for env
        if (newStateProperties.username !== undefined)
          sessions[activeEnvId].username = finalUsername
        if (newStateProperties.lastAuthMethod !== undefined)
          sessions[activeEnvId].lastAuthMethod = finalLastAuthMethod
        if (newStateProperties.token !== undefined)
          sessions[activeEnvId].token = finalToken // Handles null to clear
        if (newStateProperties.apiKey !== undefined)
          sessions[activeEnvId].apiKey = finalApiKey // Handles null to clear

        // If both token and apiKey are being cleared for the env, remove the env entry or mark as sessionless
        if (
          sessions[activeEnvId].token === undefined &&
          sessions[activeEnvId].apiKey === undefined
        ) {
          // Optionally, clean up empty session entries
          // delete sessions[activeEnvId];
        }
      }
      globalAuthStorage.set('sessionsByEnv', sessions)
    } else {
      console.warn(
        'AuthStore: No activeEnvId, cannot persist session to localStorage for a specific environment.',
      )
    }

    // Update in-memory state (always reflects current active environment)
    return {
      ...state,
      ...newStateProperties, // Apply incoming direct changes
      // Then ensure core fields are set from potentially modified inputs
      username: finalUsername,
      lastAuthMethod: finalLastAuthMethod,
      token: finalToken,
      apiKey: finalApiKey,
      isSessionActive: !!(finalApiKey || finalToken),
      decodedToken: decoded,
      expiresAt: decoded?.exp ? decoded.exp * 1000 : undefined,
      issuedAt: decoded?.iat ? decoded.iat * 1000 : undefined,
      personId: decoded?.personId,
      companyId: decoded?.companyId,
      userId: decoded?.sub,
      // Retain loading states if passed, otherwise keep existing
      isInitializing:
        newStateProperties.isInitializing !== undefined
          ? newStateProperties.isInitializing
          : state.isInitializing,
      isLoggingIn:
        newStateProperties.isLoggingIn !== undefined
          ? newStateProperties.isLoggingIn
          : state.isLoggingIn,
      isRenewing:
        newStateProperties.isRenewing !== undefined
          ? newStateProperties.isRenewing
          : state.isRenewing,
      isValidatingApiKey:
        newStateProperties.isValidatingApiKey !== undefined
          ? newStateProperties.isValidatingApiKey
          : state.isValidatingApiKey,
      isFetchingUser:
        newStateProperties.isFetchingUser !== undefined
          ? newStateProperties.isFetchingUser
          : state.isFetchingUser,
    }
  })
}

// --- Store Actions ---
export const authActions = {
  initializeSessionAndUser: async (envIdOverride?: string): Promise<void> => {
    const activeEnvId = envIdOverride || envStore.state.activeEnvRef?.id

    // _updateStateAfterAuthChange({ isInitializing: true, authError: null }); // Moved down

    // Ensure isInitializing is true from the start of this specific env init
    // regardless of who called it.
    authStore.setState((state) => ({
      ...state,
      isInitializing: true,
      authError: null,
      // Crucially, clear previous user data if switching envs before validation
      currentUser: undefined,
      token: undefined, // Clear token from previous env before loading new one
      apiKey: undefined, // Clear apiKey from previous env
      decodedToken: undefined,
      isSessionActive: false, // Assume not active until proven otherwise for the NEW env
      username: undefined,
      lastAuthMethod: undefined,
    }))

    if (!activeEnvId) {
      console.warn(
        'AuthStore: No active environment. Cannot initialize session.',
      )
      _updateStateAfterAuthChange({
        isInitializing: false,
        isSessionActive: false,
      }) // Ensure these are set again
      return
    }

    // Now that activeEnvId is confirmed, log it
    console.info(`AuthStore: Initializing session for envId: ${activeEnvId}`)

    const sessions = globalAuthStorage.get('sessionsByEnv') || {}
    const currentEnvSessionData: EnvSessionData | undefined =
      sessions[activeEnvId]

    if (
      !currentEnvSessionData ||
      (!currentEnvSessionData.apiKey && !currentEnvSessionData.token)
    ) {
      console.info(
        `AuthStore: No session data, token, or API key found for env ${activeEnvId}. Session will be inactive.`,
      )
      _updateStateAfterAuthChange({
        isSessionActive: false,
        isInitializing: false,
        token: undefined,
        apiKey: undefined,
        username: undefined,
        lastAuthMethod: undefined,
        currentUser: undefined, // Ensure user is cleared
        decodedToken: undefined,
      })
      return
    }

    if (currentEnvSessionData.apiKey) {
      console.info(
        `AuthStore: Initializing with API key for env ${activeEnvId}.`,
      )
      _updateStateAfterAuthChange({
        apiKey: currentEnvSessionData.apiKey || undefined,
        username: currentEnvSessionData.username || undefined,
        lastAuthMethod: currentEnvSessionData.lastAuthMethod || undefined,
        token: undefined,
        isSessionActive: true,
        currentUser: undefined,
        decodedToken: undefined,
      })
      if (getApiBaseFromEnvStore(activeEnvId)) {
        // Pass envId to ensure correct apiBase
        await authActions.fetchCurrentUser(activeEnvId)
      }
      _updateStateAfterAuthChange({ isInitializing: false })
      return
    }

    // Token exists for this environment
    _updateStateAfterAuthChange({
      token: currentEnvSessionData.token || undefined,
      username: currentEnvSessionData.username || undefined,
      lastAuthMethod: currentEnvSessionData.lastAuthMethod || undefined,
      apiKey: undefined,
      isSessionActive: true,
      currentUser: undefined,
      decodedToken: undefined,
    })

    const currentToken = authStore.state.token
    if (currentToken) {
      try {
        const decoded = jwt_decode<DecodedToken>(currentToken)
        authStore.setState((state) => ({
          ...state,
          decodedToken: decoded,
          personId: decoded.personId,
          companyId: decoded.companyId,
          userId: decoded.sub,
          expiresAt: decoded.exp ? decoded.exp * 1000 : undefined,
          issuedAt: decoded.iat ? decoded.iat * 1000 : undefined,
        }))
      } catch (e) {
        console.error(
          'AuthStore: Failed to decode token immediately after setting for new env.',
          e,
        )
        _updateStateAfterAuthChange({
          isSessionActive: false,
          isInitializing: false,
          token: undefined,
          apiKey: undefined,
          username: undefined,
          lastAuthMethod: undefined,
          currentUser: undefined,
          decodedToken: undefined,
        })
        return
      }
    } else {
      // Should not happen if currentEnvSessionData.token was valid
      console.error(
        'AuthStore: Token became undefined immediately after setting from currentEnvSessionData.',
      )
      _updateStateAfterAuthChange({
        isSessionActive: false,
        isInitializing: false,
        token: undefined,
        apiKey: undefined,
        username: undefined,
        lastAuthMethod: undefined,
        currentUser: undefined,
        decodedToken: undefined,
      })
      return
    }

    if (getApiBaseFromEnvStore(activeEnvId)) {
      // Pass envId to ensure correct apiBase
      console.info(
        `AuthStore: Token found for env ${activeEnvId}. Validating by fetching user.`,
      )
      await authActions.fetchCurrentUser(activeEnvId)
    } else {
      console.warn(
        `AuthStore: Token found for env ${activeEnvId}, but no apiBase. Session may be stale.`,
      )
    }
    _updateStateAfterAuthChange({ isInitializing: false })
  },

  fetchCurrentUser: async (envIdOverride?: string): Promise<void> => {
    const activeEnvId = envIdOverride || envStore.state.activeEnvRef?.id
    const apiBase = getApiBaseFromEnvStore(activeEnvId) // Use potentially overridden envId
    const {
      token,
      apiKey,
      isSessionActive: sessionInitiallyAssumedActive,
    } = authStore.state

    if (!sessionInitiallyAssumedActive || !apiBase || !activeEnvId) {
      _updateStateAfterAuthChange({ isFetchingUser: false })
      return
    }
    if (!token && !apiKey) {
      _updateStateAfterAuthChange({ isFetchingUser: false })
      return
    }

    _updateStateAfterAuthChange({ isFetchingUser: true, authError: null })
    try {
      const userInfo = await rawAuthApi.fetchUserInfo(apiBase)
      _updateStateAfterAuthChange({
        currentUser: userInfo?.data,
        isFetchingUser: false,
        // isSessionActive remains true as fetch was successful
      })
    } catch (error: any) {
      console.error('AuthStore: Failed to fetch current user info.', error)
      if (error?.status === 401) {
        console.warn(
          `AuthStore: Received 401 for env ${activeEnvId}. Clearing session for this env.`,
        )
        authStore.setState((state) => ({
          ...state,
          isFetchingUser: false,
          authError: null,
        }))
        authActions.logout()
        authActions.setLoginModalExplicitlyRequested(true)
      } else {
        _updateStateAfterAuthChange({ isFetchingUser: false, authError: error })
      }
    }
  },

  createSession: async (options: {
    username: string
    authMethod: string
    password?: string
    companyId?: string
    userType?: string
    domain?: string
    oneTimeCode?: string
  }): Promise<any> => {
    const apiBase = getApiBaseFromEnvStore()
    if (!apiBase)
      throw new Error('AuthStore: apiBase not set, cannot create session.')
    const activeEnvId = envStore.state.activeEnvRef?.id
    if (!activeEnvId)
      throw new Error('AuthStore: No active env, cannot create session.')

    _updateStateAfterAuthChange({ isLoggingIn: true, authError: null })
    try {
      const resp = await rawAuthApi.createSession(apiBase, options)
      _updateStateAfterAuthChange({
        token: resp.token,
        username: options.username,
        lastAuthMethod: options.authMethod,
        apiKey: undefined, // Clear API key on token login
        isLoggingIn: false,
        isSessionActive: true,
      })
      await authActions.fetchCurrentUser()
      return resp
    } catch (err) {
      _updateStateAfterAuthChange({
        isLoggingIn: false,
        authError: err,
        isSessionActive: false,
      })
      throw err
    }
  },

  renewSession: async (): Promise<void> => {
    const { token: currentTokenInMem, apiKey } = authStore.state
    const apiBase = getApiBaseFromEnvStore()
    const activeEnvId = envStore.state.activeEnvRef?.id

    if (apiKey || !currentTokenInMem || !apiBase || !activeEnvId) {
      if (apiKey) _updateStateAfterAuthChange({ isRenewing: false })
      return
    }
    _updateStateAfterAuthChange({ isRenewing: true, authError: null })
    try {
      const resp = await rawAuthApi.renewSession(apiBase, {
        token: currentTokenInMem,
      })
      _updateStateAfterAuthChange({
        token: resp.token,
        isRenewing: false,
        isSessionActive: true,
      })
    } catch (err: any) {
      _updateStateAfterAuthChange({
        isRenewing: false,
        authError: err,
        isSessionActive: false,
      })
      if (err.status === 401) {
        console.warn(
          `AuthStore: Session renewal failed with 401 for ${activeEnvId}. Clearing session for this env.`,
        )
        authActions.clearSessionForCurrentEnv(activeEnvId) // Pass it along
      }
      throw err
    }
  },

  validateApiKey: async (newApiKeyToValidate: string): Promise<void> => {
    const apiBase = getApiBaseFromEnvStore()
    if (!apiBase)
      throw new Error('AuthStore: apiBase not set, cannot validate API key.')
    const activeEnvId = envStore.state.activeEnvRef?.id
    if (!activeEnvId)
      throw new Error('AuthStore: No active env, cannot validate API key.')

    _updateStateAfterAuthChange({ isValidatingApiKey: true, authError: null })
    try {
      await rawAuthApi.validateApiKey(apiBase, newApiKeyToValidate)
      _updateStateAfterAuthChange({
        apiKey: newApiKeyToValidate,
        token: undefined, // Clear token
        // username might not be applicable for API key, or could be set to a generic identifier
        // For now, let's keep existing username or clear if not passed
        username: authStore.state.username, // Or undefined if API keys shouldn't have usernames
        lastAuthMethod: 'apiKey',
        isValidatingApiKey: false,
        isSessionActive: true,
      })
      await authActions.fetchCurrentUser() // Fetch user info after successful API key validation
    } catch (err) {
      _updateStateAfterAuthChange({
        isValidatingApiKey: false,
        authError: err,
        isSessionActive: false,
      })
      throw err
    }
  },

  logout: (): void => {
    // Logs out from the CURRENT active environment
    const activeEnvId = envStore.state.activeEnvRef?.id
    console.info(
      `AuthStore: Logging out from env ${
        activeEnvId || '(unknown current env)'
      }`,
    )

    // Optional: Call backend logout for current env
    // const apiBase = getApiBaseFromEnvStore();
    // if (apiBase && authStore.state.token) { rawAuthApi.logout(apiBase, authStore.state.token); }

    authActions.clearInMemorySessionState() // Clear in-memory state first

    if (activeEnvId) {
      const sessions = globalAuthStorage.get('sessionsByEnv') || {}
      if (sessions[activeEnvId]) {
        delete sessions[activeEnvId]
        globalAuthStorage.set('sessionsByEnv', sessions)
        console.info(
          `AuthStore: Removed session for env "${activeEnvId}" from localStorage.`,
        )
      }
    }
    // After clearing the specific env session, the in-memory state should reflect being logged out.
    // _updateStateAfterAuthChange will ensure isSessionActive is false from clearInMemorySessionState.
  },

  handleUnauthorizedAccess: (): void => {
    // Called on 401 from API calls
    const activeEnvId = envStore.state.activeEnvRef?.id
    console.warn(
      `AuthStore: Unauthorized access detected for env ${
        activeEnvId || '(unknown)'
      }. Clearing session for this env.`,
    )
    authActions.logout() // New: Perform a full logout
    authActions.setLoginModalExplicitlyRequested(true) // Then request login
  },

  setLoginModalExplicitlyRequested: (requested: boolean) => {
    authStore.setState((state) => ({
      ...state,
      loginModalExplicitlyRequested: requested,
    }))
  },

  // Clears only the in-memory session details for the current active environment
  clearInMemorySessionState: () => {
    authStore.setState((state) => ({
      ...state, // Preserve other state like loading flags if necessary, or reset more broadly
      token: undefined,
      apiKey: undefined,
      decodedToken: undefined,
      currentUser: undefined,
      isSessionActive: false,
      username: undefined,
      lastAuthMethod: undefined,
      expiresAt: undefined,
      issuedAt: undefined,
      personId: undefined,
      companyId: undefined,
      userId: undefined,
      authError: null, // Explicitly clear authError
    }))
  },

  // New action: Clears session for the current environment from both memory and localStorage
  clearSessionForCurrentEnv: (envIdToClear?: string) => {
    const activeEnvId = envIdToClear || envStore.state.activeEnvRef?.id
    authActions.clearInMemorySessionState() // Clear memory
    if (activeEnvId) {
      const sessions: StoredSessions =
        globalAuthStorage.get('sessionsByEnv') || {}
      if (sessions[activeEnvId]) {
        delete sessions[activeEnvId]
        globalAuthStorage.set('sessionsByEnv', sessions)
        console.info(
          `AuthStore: Cleared session for env "${activeEnvId}" from localStorage (critical error).`,
        )
      }
    }
  },

  // New utility action
  setInitializingAsTrue: () => {
    _updateStateAfterAuthChange({
      isInitializing: true,
      authError: null,
      currentUser: undefined,
      token: undefined,
      apiKey: undefined,
      decodedToken: undefined,
      isSessionActive: false,
      username: undefined,
      lastAuthMethod: undefined,
    })
  },
}

// --- Selector Hooks (optional, but recommended for components) ---
// You can create these in this file or a separate 'src/core/auth/authHooks.ts'

export const useAuthToken = () => useStore(authStore, (state) => state.token)
export const useApiKey = () => useStore(authStore, (state) => state.apiKey)
export const useAuthUsername = () =>
  useStore(authStore, (state) => state.username)
export const useDecodedToken = () =>
  useStore(authStore, (state) => state.decodedToken)
export const useAuthCompanyId = () =>
  useStore(authStore, (state) => state.companyId)
export const useCurrentUserInfo = () =>
  useStore(authStore, (state) => state.currentUser)
export const useAuthIsInitializing = () =>
  useStore(authStore, (state) => state.isInitializing)
export const useAuthIsLoggingIn = () =>
  useStore(authStore, (state) => state.isLoggingIn)
export const useIsSessionActive = () =>
  useStore(authStore, (state) => state.isSessionActive)
export const useAuthIsFetchingUser = () =>
  useStore(authStore, (state) => state.isFetchingUser)
export const useAuthIsRenewing = () =>
  useStore(authStore, (state) => state.isRenewing)
